import { HSOverlay, HSSelect } from "preline";
import { disableButton, enableButton } from "../../../lib/utility";
import { userDetailStore, UserDetailType } from "../../../stores/userDetailStore";
import { z } from "zod";
import { createFormFieldHandler } from "../../../utils/form-handler";
import { userDetailService } from "../../../services/userDetailService";
import { addToast } from "../../../toasts";

const LOGO_MAX_SIZE = 15 * 1024 * 1024;
const NAME_MAX_LENGTH = 128;
const ALLOWED_ROLES = ['designer', 'product_manager', 'marketeer', 'others'];

export function AccountDetails() {
    let currEditAccountDetail: any | UserDetailType = null;
    let userDetailStoreUnsubscribe: () => void;

    // Custom Zod schema for logo validation
    const logoSchema = z
        .instanceof(File)
        .optional()
        .refine((file) => !file || !file.size || (file.type.startsWith("image/") && file.type !== "image/avif"), {
            message: "Only image files are allowed",
        })
        .refine((file) => !file || !file.size || file.size <= LOGO_MAX_SIZE, {
            message: `File size must be less than ${LOGO_MAX_SIZE / 1024 / 1024} MB`,
        });

    const roleSchema = z
        .enum(ALLOWED_ROLES as [string, ...string[]])
        .optional();

    const formSchema = z.object({
        logo: logoSchema,
        name: z.string().max(NAME_MAX_LENGTH, `Max ${NAME_MAX_LENGTH} characters allowed`).min(1, "Name is required"),
        role: roleSchema
    });

    type FormData = z.infer<typeof formSchema>;

    let $formElem: null | HTMLFormElement = null;
    let $logoInput: null | HTMLInputElement = null;
    let $nameInput: null | HTMLInputElement = null;
    let $roleInput: null | HTMLInputElement = null;
    let $logoPreview: null | HTMLElement = null;
    let $uploadLogoButton: null | HTMLButtonElement = null;
    let $uploadLogoButtonText: null | HTMLSpanElement = null;
    let $submitButton: null | HTMLButtonElement = null;
    let $emailInput: null | HTMLInputElement = null;

    let resetFormErrors: undefined | (() => void);

    function updateUploadButtonText(hasImage: boolean) {
        if ($uploadLogoButtonText) {
            $uploadLogoButtonText.textContent = hasImage ? "Photo" : "Upload a photo";
        }
    }

    function resetForm() {
        resetLogoPreview();
        $formElem?.reset();
        updateUploadButtonText(false);
        if (resetFormErrors) resetFormErrors();
    }

    function resetLogoPreview() {
        $logoInput!.value = "";
        $logoPreview!.innerHTML = `<svg class="shrink-0 size-7" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
          stroke-linejoin="round">
          <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>`;
    };

    async function populate({ isLoading, userDetail }: { isLoading: boolean, userDetail: UserDetailType; }) {
        //wait for 2 seconds before populating
        if (isLoading) {
            disableButton($submitButton!);
            return;
        }
        if (userDetail) {
            enableButton($submitButton!);
            currEditAccountDetail = userDetail;
            const user_meta = userDetail.user_meta;
            const hasLogo = !!user_meta?.profile_pic_url;
            if (hasLogo) {
                $logoPreview!.innerHTML = `<img src="${user_meta.profile_pic_url}" class="w-full h-full object-contain" />`
            } else {
                $logoPreview!.innerHTML = `<svg class="shrink-0 size-7" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"
                stroke-linejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>`
            }
            updateUploadButtonText(hasLogo);
            $nameInput!.value = currEditAccountDetail.name;
            $emailInput!.value = currEditAccountDetail.email;
            const HsRoleSelect = HSSelect.getInstance($roleInput!) as HSSelect;
            HsRoleSelect.setValue(user_meta?.user_role || '');
        }
    }

    function init() {
        HSOverlay.autoInit();
        $formElem = document.getElementById("account-details-form") as HTMLFormElement;
        $submitButton = $formElem.querySelector('#account-detail-save') as HTMLButtonElement;
        $logoInput = $formElem.querySelector("#logo") as HTMLInputElement;
        $logoPreview = $formElem.querySelector("#logo-preview") as HTMLElement;
        $uploadLogoButton = $formElem.querySelector("#upload-user-avatar-button") as HTMLButtonElement;
        $uploadLogoButtonText = $formElem.querySelector("#upload-logo-button-text") as HTMLSpanElement;
        $nameInput = $formElem.querySelector('[name="name"]') as HTMLInputElement;
        $roleInput = $formElem.querySelector('[name="role"]') as HTMLInputElement;
        $emailInput = document.getElementById('user-email') as HTMLInputElement;
        const closeBtn = document.getElementById('close-profile-modal-btn') as HTMLButtonElement;

        closeBtn.addEventListener('click', () => {
            resetForm();
            const hasInitialImage = $logoPreview?.querySelector('img') !== null;
            updateUploadButtonText(hasInitialImage);
            populate({ isLoading: userDetailStore.loading, userDetail: userDetailStore.userDetail });
        });

        if (!($formElem && $submitButton && $logoInput && $logoPreview && $uploadLogoButton && $uploadLogoButtonText)) {
            console.error("Can't find all the Account Details form elements!");
        }

        $uploadLogoButton.addEventListener("click", () => {
            $logoInput!.click();
        });

        $logoInput.addEventListener("change", () => {
            const file = $logoInput!.files?.[0];
            if (file) {
                try {
                    logoSchema.parse(file);
                    const reader = new FileReader();
                    reader.onload = () => {
                        $logoPreview!.innerHTML = `<img src="${reader.result}" class="w-full h-full object-contain" />`;
                        updateUploadButtonText(true);
                    };
                    reader.readAsDataURL(file);
                } catch (error) {
                    if (error instanceof z.ZodError) {
                        setFieldError("logo", error.errors[0].message);
                    }
                }
            }
        });

        const { setFieldError, validateOnBlur, resetErrors } = createFormFieldHandler(
            formSchema,
            $formElem
        );

        resetFormErrors = resetErrors;
        // Add event listeners to form fields to validate on blur
        validateOnBlur();

        $formElem.addEventListener("submit", async (e) => {
            e.preventDefault();

            if (!currEditAccountDetail) {
                console.error("No Account details to update!");
                return;
            }

            const formData = new FormData($formElem!);

            // Check if a new logo is uploaded; if not, remove the logo field
            const logoFile = $logoInput!.files?.[0];
            if (!logoFile) {
                formData.delete("logo");
            }

            const data = Object.fromEntries(formData.entries()) as FormData;
            if (!data.role) {
                delete data.role;
            }

            try {
                formSchema.parse(data);
                disableButton($submitButton!);
                try {
                    const response: any = await userDetailService.update(currEditAccountDetail?.id, formData);
                    addToast({ toastBody: response.message }, "default", "success");

                    userDetailService.fetchDetail();
                    enableButton($submitButton!);
                } catch (error: any) {
                    // this catch block is for handling Server errors on the request
                    console.error("Update account details request failed: ", error);
                    addToast({ toastBody: error.message }, "default", "error");
                    error.errors && Object.keys(error.errors).forEach((field) => {
                        // Display each error message for the corresponding field
                        setFieldError(field as keyof FormData, error.errors[field][0]);
                    });
                }
            } catch (error) {
                // this catch block is for handling Frontend form validation
                if (error instanceof z.ZodError) {
                    error.errors.forEach((err) => {
                        setFieldError(err.path[0] as keyof FormData, err.message);
                    });
                }
            } finally {
                enableButton($submitButton!);
            }
        });
        const hasInitialImage = $logoPreview?.querySelector('img') !== null;
        updateUploadButtonText(hasInitialImage);
        userDetailStoreUnsubscribe = userDetailStore.subscribe(({ isLoading, userDetail }) => populate({ isLoading, userDetail })); // populate the loading state if workspaces loading
        populate({ isLoading: userDetailStore.loading, userDetail: userDetailStore.userDetail });
    }

    function cleanUp() {
        if (userDetailStoreUnsubscribe) {
            userDetailStoreUnsubscribe();
        }
    }

    function render() {
        return /*html*/`<form id="account-details-form"><div class="py-6 sm:py-8 space-y-5 border-t border-gray-200 first:border-t-0 dark:border-neutral-700">
    
        <h2 class="font-semibold text-gray-800 dark:text-neutral-200">
            Account Details
        </h2>

        <!-- Grid -->
        <div class="grid sm:grid-cols-12 gap-y-1.5 sm:gap-y-0 sm:gap-x-5">
            <div class="sm:col-span-4 xl:col-span-3 2xl:col-span-2">
                <label class="sm:mt-2.5 inline-block text-sm text-gray-500 dark:text-neutral-500">
                    Profile Pic
                </label>
            </div>
            <!-- End Col -->

            <div class="sm:col-span-8 xl:col-span-8 mt-4 sm:mt-0">
                <!-- Logo Upload Group -->
                <div class="flex flex-wrap items-center gap-3 sm:gap-5">
                    <span id="logo-preview"
                        class="flex shrink-0 p-0 overflow-hidden justify-center items-center size-20 border-2 border-dotted border-gray-300 text-gray-400 rounded-full dark:border-neutral-700 dark:text-neutral-600">
                        <svg class="shrink-0 size-7" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"
                            stroke-linecap="round" stroke-linejoin="round">
                            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                            <circle cx="9" cy="9" r="2"></circle>
                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                        </svg>
                    </span>

                    <div class="grow">
                        <div class="flex items-center gap-x-2">
                            <input type="file" id="logo" name="logo" class="hidden" accept="image/*" />
                            <button type="button" id="upload-user-avatar-button"
                                class="py-2 px-3 inline-flex items-center gap-x-2 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-none dark:bg-neutral-800 focus:outline-none focus:bg-gray-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700">
                                <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="17 8 12 3 7 8"></polyline>
                                    <line x1="12" x2="12" y1="3" y2="15"></line>
                                </svg>
                                <span id="upload-logo-button-text">Upload a Photo</span>
                            </button>
                            <!-- <button type="button" class="py-2 px-3 inline-flex items-center gap-x-2 text-xs font-medium rounded-lg border border-transparent bg-white text-red-500 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:bg-gray-50 dark:bg-neutral-800 dark:text-red-500 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700" disabled="">
            <svg class="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash-2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
          </button> -->
                        </div>
                        <p class="mt-2 text-xs text-gray-500 dark:text-neutral-500">
                            Size upto 15 MB
                        </p>
                        <p class="logo-error hidden text-sm text-red-600 mt-2"></p>
                    </div>
                </div>
                <!-- End Logo Upload Group -->
            </div>
            <!-- End Col -->
        </div>
        <!-- End Grid -->

        <!-- Grid -->
        <div class="grid sm:grid-cols-12 gap-y-1.5 sm:gap-y-0 sm:gap-x-5">
            <div class="sm:col-span-4 xl:col-span-3 2xl:col-span-2">
                <label for="hs-pro-dappinm" class="sm:mt-2.5 inline-block text-sm text-gray-500 dark:text-neutral-500">
                    Name
                </label>
            </div>
            <!-- End Col -->

            <div class="sm:col-span-8 xl:col-span-8">
                <input id="hs-pro-dappinm" type="text" name="name" maxlength="${NAME_MAX_LENGTH}"
                    class="py-2 px-3 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
                    placeholder="Enter your name here">

                <p class="mt-2 text-sm text-gray-500 dark:text-neutral-500">
                    Enter your full name, or a display name you are comfortable with.
                </p>
            </div>
            <!-- End Col -->
        </div>
        <!-- End Grid -->
        <!-- End Grid -->

        <!-- Grid -->
        <div class="grid sm:grid-cols-12 gap-y-1.5 sm:gap-y-0 sm:gap-x-5">
            <div class="sm:col-span-4 xl:col-span-3 2xl:col-span-2">
                <label for="user-email" class="sm:mt-2.5 inline-block text-sm text-gray-500 dark:text-neutral-500">
                    Email
                </label>
            </div>
            <!-- End Col -->

            <div class="sm:col-span-8 xl:col-span-8">
                <input disabled id="user-email" type="text"
                    class="py-2 px-3 block w-full border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-90 disabled:bg-gray-200 disabled:pointer-events-none dark:bg-transparent dark:border-neutral-700 dark:text-neutral-300 dark:placeholder:text-white/60 dark:focus:ring-neutral-600"
                    placeholder="Enter your email here">

                <p class="mt-2 text-sm text-gray-500 dark:text-neutral-500">
                    Contact us at <a class="text-blue-600 hover:text-black"
                        href="mailto:support@feedspace.io">support@feedspace.io</a> to change email.
                </p>
            </div>
            <!-- End Col -->
        </div>
        <!-- End Grid -->

        <!-- Grid -->
        <div class="grid sm:grid-cols-12 gap-y-1.5 sm:gap-y-0 sm:gap-x-5">
            <div class="sm:col-span-4 xl:col-span-3 2xl:col-span-2">
                <label class="sm:mt-2.5 inline-block text-sm text-gray-500 dark:text-neutral-500">
                    Your Role
                </label>
            </div>
            <!-- End Col -->
            <div class="sm:col-span-8 xl:col-span-8">
                <!-- Select -->
                <div class="relative block">
                    <select id="role" name="role" data-hs-select='{
          "placeholder": "Select Use Case",
          "toggleTag": "<button type=\\"button\\" aria-expanded=\\"false\\"><span data-title></span></button>",
          "toggleClasses": "hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-2 ps-3 pe-9 flex gap-x-2 text-nowrap w-full cursor-pointer bg-white border border-gray-200 rounded-lg text-start text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-400 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600",

          "dropdownClasses": "end-0 w-full max-h-72 p-1 space-y-0.5 z-50 overflow-hidden overflow-y-auto bg-white rounded-xl shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500 dark:bg-neutral-900",
          "optionClasses": "hs-selected:bg-gray-100 dark:hs-selected:bg-neutral-800 py-2 px-4 w-full text-sm text-gray-800 cursor-pointer hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 dark:bg-neutral-900 dark:hover:bg-neutral-800 dark:text-neutral-200 dark:focus:bg-neutral-800",
          "optionTemplate": "<div class=\\"flex justify-between items-center w-full\\"><span data-title></span><span class=\\"hidden hs-selected:block\\"><svg class=\\"shrink-0 size-3.5 text-gray-800 dark:text-neutral-200\\" xmlns=\\"http:.w3.org/2000/svg\\" width=\\"24\\" height=\\"24\\" viewBox=\\"0 0 24 24\\" fill=\\"none\\" stroke=\\"currentColor\\" stroke-width=\\"2\\" stroke-linecap=\\"round\\" stroke-linejoin=\\"round\\"><polyline points=\\"20 6 9 17 4 12\\"/></svg></span></div>"
        }' class="hidden">
                        <option value="">Select Use Case</option>
                        <option value="designer">Designer</option>
                        <option value="product_manager">Product Manager</option>
                        <option value="marketeer">Marketeer</option>
                        <option value="others">Others</option>
                    </select>

                    <div class="absolute top-1/2 end-2.5 -translate-y-1/2">
                        <svg class="shrink-0 size-3.5 text-gray-500 dark:text-neutral-500"
                            xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="m7 15 5 5 5-5" />
                            <path d="m7 9 5-5 5 5" />
                        </svg>
                    </div>
                </div>
                <!-- End Select -->

                <p class="mt-2 text-sm text-gray-500 dark:text-neutral-500">
                    Your role helps Feedspace to show personalized suggestions.
                </p>
            </div>
            <!-- End Col -->
        </div>
        <!-- End Grid -->

        <!-- Button Group -->
        <div class="grid sm:grid-cols-12 gap-y-1.5 sm:gap-y-0 sm:gap-x-5">
            <div class="sm:col-span-4 xl:col-span-3 2xl:col-span-2"></div>

            <div class="sm:col-span-8 xl:col-span-8">
                <div class="flex gap-x-3">
                    <button type="submit" id="account-detail-save"
                        class="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <span class="btn-label flex gap-x-1 items-center">
                        Save Changes
                        </span>
                    </button>
                </div>
            </div>
        </div>
    
    <!-- End Button Group -->
</div></form>`;
    }

    return { render, init, resetForm, populate, cleanUp };
}